import React, { useEffect, useState } from "react";
import imageCompression from "browser-image-compression";
import heic2any from "heic2any";
import { INSPECTION_STATUS } from "../../../../constant/constant";
import parse from "html-react-parser";
import Loader from "../../../../library/common/Loader";

const PreviousOpenItem = ({
  filterPreviousData,
  reportId,
  showItemId = false,
}) => {
  const isClosedHere = (itemPrev) =>
    itemPrev?.status === INSPECTION_STATUS?.Closed &&
    itemPrev?.linkedReportId === reportId;

  const [compressedImages, setCompressedImages] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchImageAsFile = async (url) => {
    if (!url) {
      throw new Error("Invalid image url");
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${url}`);
    }

    const blob = await response.blob();

    const extension = url.split(".").pop()?.toLowerCase() || "jpg";

    return new File([blob], `image.${extension}`, {
      type: blob.type || `image/${extension}`,
    });
  };

  const convertHeicToJpeg = async (file) => {
    const convertedBlob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.8,
    });

    const jpegBlob = Array.isArray(convertedBlob)
      ? convertedBlob[0]
      : convertedBlob;

    return new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), {
      type: "image/jpeg",
    });
  };

  useEffect(() => {
    const compressImages = async () => {
      setLoading(true);

      try {
        const imagesByLocation = {};

        await Promise.all(
          filterPreviousData?.map(async (itemPrev, locationIndex) => {
            const files = await Promise.all(
              itemPrev?.files?.map(async (img) => {
                try {
                  if (!img?.filePath) {
                    return null;
                  }

                  const file = await fetchImageAsFile(img.filePath);

                  const isHeic =
                    img.filePath?.toLowerCase()?.endsWith(".heic") ||
                    img.filePath?.toLowerCase()?.endsWith(".heif") ||
                    file.type?.toLowerCase()?.includes("heic") ||
                    file.type?.toLowerCase()?.includes("heif");

                  let fileToProcess = file;

                  if (isHeic) {
                    fileToProcess = await convertHeicToJpeg(file);
                  }

                  const compressedFile = await imageCompression(fileToProcess, {
                    maxSizeMB: 0.1,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                  });

                  return {
                    filePath: URL.createObjectURL(compressedFile),
                    fileId: img.fileId,
                  };
                } catch (error) {
                  console.error(
                    "Image processing failed:",
                    img?.filePath,
                    error,
                  );

                  return null;
                }
              }) || [],
            );

            imagesByLocation[locationIndex] = files.filter(Boolean);
          }),
        );

        setCompressedImages(imagesByLocation);
      } catch (error) {
        console.error("Image compression error:", error);
      } finally {
        setLoading(false);
      }
    };

    compressImages();

    return () => {
      Object.values(compressedImages).forEach((images) => {
        images?.forEach((img) => {
          if (img?.filePath && img.filePath.startsWith("blob:")) {
            URL.revokeObjectURL(img.filePath);
          }
        });
      });
    };
  }, [filterPreviousData]);

  return (
    <>
      {loading && <Loader />}

      {filterPreviousData?.map((itemPrev, locationIndex) => (
        <tr className="tableBody" key={locationIndex}>
          {showItemId && <td>{itemPrev?.locationId}</td>}
          <td>{itemPrev?.location}</td>

          <td>
            <div className="itemFondText">
              <span>
                {itemPrev?.item}
                {itemPrev?.itemDetail && <> - </>}
              </span>

              {itemPrev?.itemDetail && (
                <span>{parse(unescape(itemPrev?.itemDetail))}</span>
              )}
            </div>
          </td>

          <td>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              {compressedImages[locationIndex]?.map((img) => (
                <img
                  key={img.fileId}
                  src={img.filePath}
                  alt="inspection"
                  className="imgViewReport"
                />
              ))}
            </div>
          </td>

          <td>
            <div className="statuss">
              {isClosedHere(itemPrev) ? (
                <div className="closed">Closed</div>
              ) : (
                <div className="open">Open</div>
              )}
            </div>
          </td>

          <td className="swinspectiondownloadprint">
            {isClosedHere(itemPrev) ? (
              <>{itemPrev?.actionTaken || itemPrev?.closedAction}</>
            ) : (
              <>
                {itemPrev?.comment?.map((item, index) => (
                  <div key={index}>{item?.comment}</div>
                ))}
              </>
            )}
          </td>

          <td>{itemPrev?.dateCreatedFormatted}</td>
        </tr>
      ))}
    </>
  );
};

export default PreviousOpenItem;
